import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AlertTriangle, Lightbulb, RefreshCw, TrendingUp } from 'lucide-react';
import {
  dineroRedondo,
  horaEnPalabras,
  type DiaSerie,
  type TableroEstadisticas,
} from '@shared/estadisticas';

/**
 * Sprint 170 — **Cómo va el negocio.**
 *
 * José, 2026-11-01: *"lo que vale es darle estrategias de venta en base al comportamiento de su
 * negocio"*. Reportes contesta "¿cuánto entró este periodo?"; esta pantalla contesta las cuatro
 * preguntas que la dueña se hace sola —¿cómo voy?, ¿qué me deja más?, ¿cuándo se vende?, ¿qué no
 * se vende?— y, arriba de todo, qué hacer con eso.
 *
 * ## Por qué así
 *
 * - **Cada gráfica lleva su frase arriba.** La dueña no interpreta ejes: lee "Esta semana llevas
 *   $8,400: 12% más que la pasada". La gráfica es la prueba de la frase, no al revés.
 * - **Los consejos van primero**, con el botón a donde se actúa. Un consejo sin salida es una
 *   advertencia sin acción, que es un "nunca" del oficio.
 * - **Gráficas hechas aquí**, con HTML y Tailwind: ninguna librería nueva que auditar ni que
 *   mantener, y se ven igual que el resto del sistema.
 * - **Un solo color de datos** (azul): el color de marca de cada negocio es para botones, no para
 *   cifras. La semana pasada va en el mismo azul más claro, con su leyenda.
 * - El cálculo vive en el servidor (`shared/estadisticas.ts`); la pantalla no suma nada.
 */

const AZUL = '#256abf';
const AZUL_CLARO = '#9ec5f4';

export function EstadisticasPage() {
  const navigate = useNavigate();
  const [tablero, setTablero] = useState<TableroEstadisticas | null>(null);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const cargar = useCallback(async () => {
    setCargando(true);
    const r = await window.api.estadisticas.tablero();
    if (r.ok) {
      setTablero(r.data);
      setError(null);
    } else {
      // Un número viejo se lee como un número: si falla, se borra lo anterior.
      setTablero(null);
      setError(r.error);
    }
    setCargando(false);
  }, []);

  useEffect(() => {
    void cargar();
  }, [cargar]);

  return (
    <div className="space-y-4">
      <header className="flex flex-wrap items-start gap-3">
        <TrendingUp className="mt-1 h-7 w-7 text-primary" aria-hidden="true" />
        <div className="flex-1">
          <h1 className="text-2xl font-bold">Cómo va el negocio</h1>
          <p className="text-sm text-slate-700">
            Las últimas 8 semanas y la que va. El dinero cuenta el día en que entró, igual que en tu
            corte de caja.
          </p>
        </div>
        <button onClick={() => void cargar()} className="btn-ghost min-h-[44px] text-sm">
          <RefreshCw className="h-4 w-4" aria-hidden="true" />
          Actualizar
        </button>
      </header>

      {error ? (
        <div className="card border-2 border-amber-300 bg-amber-50">
          <div className="flex items-start gap-3">
            <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-amber-700" aria-hidden="true" />
            <div>
              <div className="font-semibold text-amber-900">No se pudieron traer los números.</div>
              <p className="mt-1 text-sm text-amber-900">{error}</p>
              <button onClick={() => void cargar()} className="btn-ghost mt-2 min-h-[44px] text-sm">
                Volver a intentar
              </button>
            </div>
          </div>
        </div>
      ) : cargando && !tablero ? (
        <div className="card py-10 text-center text-slate-600">Trayendo los números…</div>
      ) : tablero ? (
        <>
          <Consejos tablero={tablero} onIr={(ruta) => navigate(ruta)} />
          <ComoVoy tablero={tablero} />
          <QueDeja tablero={tablero} />
          <CuandoSeVende tablero={tablero} />
          <LoQueNoSale tablero={tablero} onIr={(ruta) => navigate(ruta)} />
        </>
      ) : null}
    </div>
  );
}

// ── Ideas ────────────────────────────────────────────────────────────────────

function Consejos({
  tablero,
  onIr,
}: {
  tablero: TableroEstadisticas;
  onIr: (ruta: string) => void;
}) {
  if (tablero.consejos.length === 0) return null;
  return (
    <section
      className="card space-y-3 border-2 border-emerald-200 bg-emerald-50"
      aria-labelledby="ideas"
    >
      <h2 id="ideas" className="flex items-center gap-2 text-lg font-bold text-emerald-950">
        <Lightbulb className="h-5 w-5" aria-hidden="true" />
        Ideas para vender más
      </h2>
      <ul className="space-y-3">
        {tablero.consejos.map((c) => (
          <li key={c.id} className="rounded-lg border border-emerald-200 bg-white p-4">
            <div className="font-semibold text-slate-900">{c.titulo}</div>
            <p className="mt-1 text-sm text-slate-700">{c.detalle}</p>
            {c.accion && (
              <button
                type="button"
                onClick={() => onIr(c.accion!.ruta)}
                className="btn-ghost mt-3 min-h-[44px] border-2 border-emerald-600 text-sm text-emerald-900"
              >
                {c.accion.texto}
              </button>
            )}
          </li>
        ))}
      </ul>
    </section>
  );
}

// ── ¿Cómo voy? ───────────────────────────────────────────────────────────────

function ComoVoy({ tablero }: { tablero: TableroEstadisticas }) {
  const { dias, semanas, frase } = tablero.comoVoy;
  const maxDia = Math.max(1, ...dias.map((d) => Math.max(d.monto, d.montoSemanaPasada)));
  const maxSemana = Math.max(1, ...semanas.map((s) => s.monto));

  return (
    <section className="card space-y-4" aria-labelledby="como-voy">
      <div>
        <h2 id="como-voy" className="text-lg font-bold">
          ¿Cómo voy?
        </h2>
        <p className="mt-1 text-base text-slate-800">{frase}</p>
      </div>

      {/* Dos semanas en cero no se dibujan: un rectángulo vacío se lee como "no cargó". La frase ya lo dice. */}
      {dias.some((d) => d.monto > 0 || d.montoSemanaPasada > 0) && (
        <div>
          <Leyenda
            elementos={[
              { color: AZUL, texto: 'Esta semana' },
              { color: AZUL_CLARO, texto: 'La semana pasada' },
            ]}
          />
          <div
            className="mt-2 grid grid-cols-7 gap-2"
            role="img"
            aria-label="Ventas de cada día de esta semana junto a las de la semana pasada"
          >
            {dias.map((d) => (
              <ColumnaDia key={d.fecha} d={d} max={maxDia} />
            ))}
          </div>
        </div>
      )}

      <div>
        <h3 className="text-sm font-semibold text-slate-700">Semana por semana</h3>
        <div
          className="mt-2 flex h-36 items-end gap-2"
          role="img"
          aria-label="Ventas de cada una de las últimas 9 semanas"
        >
          {semanas.map((s) => (
            <div key={s.inicio} className="flex h-full flex-1 flex-col items-center justify-end">
              <span className="mb-1 text-sm font-semibold text-slate-700">
                {dineroCorto(s.monto)}
              </span>
              <div
                className="w-full max-w-[44px] rounded-t"
                title={`Semana del ${s.etiqueta}: ${dineroRedondo(s.monto)}${s.enCurso ? ' (va en curso)' : ''}`}
                style={{
                  height: `${s.monto > 0 ? Math.max(2, (s.monto / maxSemana) * 100) : 0}%`,
                  background: s.enCurso
                    ? `repeating-linear-gradient(45deg, ${AZUL}, ${AZUL} 4px, ${AZUL_CLARO} 4px, ${AZUL_CLARO} 8px)`
                    : AZUL,
                }}
              />
              <span className="mt-1 text-sm text-slate-700">{s.etiqueta}</span>
            </div>
          ))}
        </div>
        <p className="mt-1 text-sm text-slate-600">
          La barra rayada es la semana que va: todavía no termina.
        </p>
      </div>
    </section>
  );
}

function ColumnaDia({ d, max }: { d: DiaSerie; max: number }) {
  const alto = (v: number) => `${Math.max(v > 0 ? 3 : 0, (v / max) * 100)}%`;
  return (
    <div className="flex flex-col items-center">
      <span className="h-5 text-sm font-semibold text-slate-800">
        {!d.futuro && d.monto > 0 ? dineroCorto(d.monto) : ''}
      </span>
      <div className="flex h-36 w-full items-end justify-center gap-[2px]">
        <div
          className="w-1/2 max-w-[28px] rounded-t"
          style={{ height: d.futuro ? '0%' : alto(d.monto), background: AZUL }}
          title={d.futuro ? 'Todavía no llega' : `Este ${d.dia}: ${dineroRedondo(d.monto)}`}
        />
        <div
          className="w-1/2 max-w-[28px] rounded-t"
          style={{ height: alto(d.montoSemanaPasada), background: AZUL_CLARO }}
          title={`El ${d.dia} pasado: ${dineroRedondo(d.montoSemanaPasada)}`}
        />
      </div>
      <span className={`mt-1 text-sm ${d.esHoy ? 'font-bold text-slate-900' : 'text-slate-700'}`}>
        {d.esHoy ? 'hoy' : d.dia}
      </span>
    </div>
  );
}

// ── ¿Qué me deja más dinero? ─────────────────────────────────────────────────

function QueDeja({ tablero }: { tablero: TableroEstadisticas }) {
  const { productos, frase } = tablero.queDeja;
  const max = Math.max(1, ...productos.map((p) => p.ingreso));
  return (
    <section className="card space-y-3" aria-labelledby="que-deja">
      <div>
        <h2 id="que-deja" className="text-lg font-bold">
          ¿Qué me trae más dinero?
        </h2>
        <p className="mt-1 text-base text-slate-800">{frase}</p>
      </div>
      {productos.length > 0 && (
        <ul className="space-y-2" aria-label="Lo que más dinero metió en las últimas 4 semanas">
          {productos.map((p) => (
            <li
              key={p.clave}
              className="grid grid-cols-[minmax(8rem,14rem)_1fr_auto] items-center gap-3"
            >
              <span className="truncate text-sm font-medium text-slate-900" title={p.nombre}>
                {p.nombre}
              </span>
              <div className="h-5 rounded bg-slate-100">
                <div
                  className="h-5 rounded"
                  style={{ width: `${Math.max(1, (p.ingreso / max) * 100)}%`, background: AZUL }}
                  title={`${p.nombre}: ${dineroRedondo(p.ingreso)}, ${p.cantidad} vendidos`}
                />
              </div>
              <span className="whitespace-nowrap text-right text-sm text-slate-800">
                <strong>{dineroRedondo(p.ingreso)}</strong> · {p.porcentaje}% · {p.cantidad}{' '}
                {p.cantidad === 1 ? 'vez' : 'veces'}
              </span>
            </li>
          ))}
        </ul>
      )}
      <p className="text-sm text-slate-700">
        Es lo que entró por cada cosa en las últimas 4 semanas. Para saber cuánto te queda de
        ganancia hace falta el costo de cada producto.
      </p>
    </section>
  );
}

// ── ¿Cuándo se vende? ────────────────────────────────────────────────────────

function CuandoSeVende({ tablero }: { tablero: TableroEstadisticas }) {
  const { porDia, porHora, momentos, horaDesde, horaHasta, frase } = tablero.cuandoSeVende;
  const maxDia = Math.max(1, ...porDia.map((d) => d.promedio));
  const horas =
    horaDesde !== null && horaHasta !== null
      ? Array.from({ length: horaHasta - horaDesde + 1 }, (_, i) => horaDesde + i)
      : [];
  const maxHora = Math.max(1, ...horas.map((h) => porHora[h]));

  return (
    <section className="card space-y-5" aria-labelledby="cuando">
      <div>
        <h2 id="cuando" className="text-lg font-bold">
          ¿Qué días y a qué horas se vende?
        </h2>
        <p className="mt-1 text-base text-slate-800">{frase}</p>
      </div>

      <div>
        <h3 className="text-sm font-semibold text-slate-700">Lo que entra en un día normal</h3>
        <div
          className="mt-2 grid grid-cols-7 gap-2"
          role="img"
          aria-label="Venta promedio por día de la semana"
        >
          {porDia.map((d) => (
            <div key={d.indice} className="flex flex-col items-center">
              <span className="h-5 text-sm font-semibold text-slate-800">
                {d.promedio > 0 ? dineroCorto(d.promedio) : ''}
              </span>
              <div className="flex h-28 w-full items-end justify-center">
                {d.promedio > 0 ? (
                  <div
                    className="w-2/3 max-w-[44px] rounded-t"
                    style={{
                      height: `${Math.max(3, (d.promedio / maxDia) * 100)}%`,
                      background: AZUL,
                    }}
                    title={`Un ${d.dia} normal: ${dineroRedondo(d.promedio)}`}
                  />
                ) : (
                  // Un hueco en blanco se lee como "no cargó". Se dice con palabras.
                  <span className="mb-2 text-center text-sm text-slate-600">Sin ventas</span>
                )}
              </div>
              <span className="mt-1 text-sm text-slate-700">{d.dia}</span>
            </div>
          ))}
        </div>
      </div>

      {/*
        Sprint 170 (revisión de diseño) — Antes había aquí un mapa de colores día × hora. Sólo se leía
        por color y pasando el ratón, y el cuadro más claro casi no se distinguía de "sin venta".
        Se cambió por lo que la dueña de verdad pregunta: a qué hora entra el dinero (con su cifra
        escrita) y cuáles son los momentos fuertes, en una lista.
      */}
      {horas.length > 0 && (
        <div className="grid gap-6 lg:grid-cols-[2fr_1fr]">
          <div>
            <h3 className="text-sm font-semibold text-slate-700">
              A qué hora se pide, en una semana normal
            </h3>
            <div
              className="mt-2 flex h-36 items-end gap-1"
              role="img"
              aria-label="Venta de una semana normal por hora del día"
            >
              {horas.map((h) => (
                <div key={h} className="flex h-full flex-1 flex-col items-center justify-end">
                  <span className="mb-1 text-xs font-semibold text-slate-800">
                    {porHora[h] > 0 ? dineroCorto(porHora[h]) : ''}
                  </span>
                  <div
                    className="w-full max-w-[40px] rounded-t"
                    style={{
                      height: `${porHora[h] > 0 ? Math.max(3, (porHora[h] / maxHora) * 100) : 0}%`,
                      background: AZUL,
                    }}
                    title={`${horaEnPalabras(h)}: ${dineroRedondo(porHora[h])} en una semana normal`}
                  />
                  <span className="mt-1 text-sm text-slate-700">{horaCorta(h)}</span>
                </div>
              ))}
            </div>
            <p className="mt-1 text-sm text-slate-600">Cuenta la hora en que se tomó el pedido.</p>
          </div>
          {momentos.length > 0 && (
            <div>
              <h3 className="text-sm font-semibold text-slate-700">Tus momentos más fuertes</h3>
              <ol className="mt-2 space-y-2">
                {momentos.map((m, i) => (
                  <li
                    key={`${m.indice}-${m.hora}`}
                    className="flex items-baseline justify-between gap-3 rounded-lg bg-slate-50 px-3 py-2"
                  >
                    <span className="text-sm text-slate-900">
                      <strong>{i + 1}.</strong> Los {m.dia}, {m.franja}
                    </span>
                    <span className="whitespace-nowrap text-sm font-semibold text-slate-800">
                      {dineroRedondo(m.promedio)}
                    </span>
                  </li>
                ))}
              </ol>
              <p className="mt-1 text-sm text-slate-600">Lo que entra en promedio en esa hora.</p>
            </div>
          )}
        </div>
      )}
    </section>
  );
}

// ── ¿Qué no se vende? ────────────────────────────────────────────────────────

function LoQueNoSale({
  tablero,
  onIr,
}: {
  tablero: TableroEstadisticas;
  onIr: (ruta: string) => void;
}) {
  const { productos, frase } = tablero.quietos;
  return (
    <section className="card space-y-3" aria-labelledby="no-sale">
      <div>
        <h2 id="no-sale" className="text-lg font-bold">
          ¿Qué no se vende o se vende poco?
        </h2>
        <p className="mt-1 text-base text-slate-800">{frase}</p>
      </div>
      {productos.length > 0 && (
        <>
          <ul className="divide-y divide-slate-200 rounded-lg border border-slate-200">
            {productos.map((p) => (
              <li
                key={p.clave}
                className="flex flex-wrap items-center justify-between gap-2 px-4 py-3"
              >
                <span className="font-medium text-slate-900">
                  {p.nombre}
                  {p.tipo === 'paquete' && (
                    <span className="ml-2 text-xs text-slate-600">(paquete)</span>
                  )}
                </span>
                <span className="text-sm text-slate-700">
                  {p.cantidad === 0
                    ? p.dentroDePaquete > 0
                      ? 'Sólo dentro de paquetes'
                      : 'No se vendió'
                    : `Se vendió ${p.cantidad} ${p.cantidad === 1 ? 'vez' : 'veces'}`}
                  {p.dentroDePaquete > 0 &&
                    ` · salió en ${p.dentroDePaquete} ${p.dentroDePaquete === 1 ? 'paquete' : 'paquetes'}`}
                </span>
              </li>
            ))}
          </ul>
          <p className="text-sm text-slate-700">
            Puedes meterlo en un paquete junto con lo que más se vende para que lo prueben, ponerle
            una promoción, o sacarlo del menú.
          </p>
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={() => onIr('/paquetes')}
              className="btn-primary min-h-[44px] text-sm"
            >
              Armar un paquete
            </button>
            <button
              type="button"
              onClick={() => onIr('/promos')}
              className="btn-ghost min-h-[44px] text-sm"
            >
              Crear una promoción
            </button>
          </div>
        </>
      )}
    </section>
  );
}

// ── Piezas ───────────────────────────────────────────────────────────────────

function Leyenda({ elementos }: { elementos: Array<{ color: string; texto: string }> }) {
  return (
    <div className="flex flex-wrap gap-4 text-sm text-slate-700">
      {elementos.map((e) => (
        <span key={e.texto} className="flex items-center gap-2">
          <span
            className="inline-block h-3 w-3 rounded-sm"
            style={{ background: e.color }}
            aria-hidden="true"
          />
          {e.texto}
        </span>
      ))}
    </div>
  );
}

/** 13 → "1pm": en el mostrador nadie dice "las 13". */
function horaCorta(h: number): string {
  return `${h % 12 || 12}${h < 12 ? 'am' : 'pm'}`;
}

/** "$8.4 mil" para que quepa encima de una barra; la cifra completa va en el globo. */
function dineroCorto(centavos: number): string {
  const pesos = centavos / 100;
  if (pesos >= 1000) {
    const miles = pesos / 1000;
    return `$${miles >= 10 ? Math.round(miles) : miles.toFixed(1).replace(/\.0$/, '')} mil`;
  }
  return `$${Math.round(pesos)}`;
}
